'use client';

import React from 'react';

import { useParams } from 'next/navigation';

import styles from './styles.module.css';

import { SellerNavigation } from '@/components/SellerNavigation';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useFetchBatchShopByUsername } from '@/modules/shop/hooks/useFetchBatchShopByUsername';
import { type BatchShopByUsernameResponse } from '@/modules/shop/types';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  usernames: string[];
  title?: string;
};

const REQUIRED_SELLERS_LENGTH = 4;
const REQUIRED_PRODUCTS_LENGTH = 3;

export function PageHeaderSellerNavigation({ usernames, title }: Props) {
  const [_, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const { slug } = useParams<{ slug: string }>();
  const moduleName = `explore_${slug}_seller_nav`;

  const { data: shopByUsernameData, isLoading } =
    useFetchBatchShopByUsername(usernames);

  const shops = shopByUsernameData?.data || [];
  const hasRequiredProductsAmount = shops.every(
    (seller: BatchShopByUsernameResponse) =>
      seller.products && REQUIRED_PRODUCTS_LENGTH <= seller.products.length
  );
  const hasRequiredShopsAmount = REQUIRED_SELLERS_LENGTH <= shops.length;
  const hasRequiredData = hasRequiredShopsAmount && hasRequiredProductsAmount;

  function handleItemClick(item: BatchShopByUsernameResponse, index: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName,
      itemClicked: item.username,
      itemId: String(item.id),
      itemPosition: index,
      schemaVersion: '3.0',
    });
  }

  if (!hasRequiredData) {
    return null;
  }

  if (isLoading || hasRequiredData) {
    return (
      <SellerNavigation
        className={styles.sellerNavigation}
        title={title}
        items={shops.sort(
          (a, b) =>
            usernames.indexOf(a.username) - usernames.indexOf(b.username)
        )}
        onItemClick={handleItemClick}
        isLoading={isLoading}
      />
    );
  }

  return null;
}
